import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import { gt } from 'ember-truth-helpers';
import DateRanger from 'garaje/components/date-ranger';
import EventLogDropdownFilter from 'garaje/components/event-log/event-log-dropdown-filter';
import UiButton from 'garaje/components/ui-button';
import preventDefault from 'garaje/helpers/prevent-default';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type SessionService from 'garaje/services/session';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import urlBuilder from 'garaje/utils/url-builder';
import { cached } from 'tracked-toolbox';

interface EventLogFiltersSignature {
  Args: {
    isGlobal?: boolean;
    search: string;
    locationId?: number;
    productAreas?: string[];
    actors?: string[];
    eventTypes?: string[];
    dateFrom: Date;
    dateTo: Date;
    onSearchInput: (event: Event) => void;
    onSearchClear: () => void;
    onLocationChange: (locationId: number | null) => void;
    onProductAreasChange: (productAreas: string[]) => void;
    onActorsChange: (actors: string[]) => void;
    onEventTypesChange: (eventTypes: string[]) => void;
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
  };
}

interface LocationOption {
  disabled: boolean;
  location: SkinnyLocationModel;
}

interface EventLogFacets {
  event: string[];
  actorType: string[];
  productArea: string[];
  actorName: string[];
}

export default class EventLogFilters extends Component<EventLogFiltersSignature> {
  @service declare metrics: MetricsService;
  @service declare session: SessionService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked locations: SkinnyLocationModel[] = [];
  @tracked facets: EventLogFacets = {
    event: [],
    actorType: [],
    productArea: [],
    actorName: [],
  };

  constructor(owner: unknown, args: EventLogFiltersSignature['Args']) {
    super(owner, args);
    if (this.isGlobal) {
      void this.loadLocations.perform();
    }
    if (this.showAdvancedFilters) {
      void this.loadFacets.perform();
    }
  }

  get isGlobal(): boolean {
    return this.args.isGlobal ?? false;
  }

  get showAdvancedFilters(): boolean {
    return this.featureFlags.isEnabled('event-log-advanced-filters');
  }

  @cached
  get locationOptions(): Array<LocationOption> {
    return this.locations.map((location) => ({ disabled: false, location }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.args.locationId) return null;
    return this.locationOptions.find((option) => option.location.id === this.args.locationId!.toString()) || null;
  }

  get selectedProductAreas(): string[] {
    return this.args.productAreas ?? [];
  }

  get selectedActors(): string[] {
    return this.args.actors ?? [];
  }

  get selectedEventTypes(): string[] {
    return this.args.eventTypes ?? [];
  }

  loadLocations = restartableTask(async () => {
    try {
      if (!this.skinnyLocations.isLoaded) {
        await this.skinnyLocations.loadAllTask.perform();
      }
      this.locations = this.skinnyLocations.content;
    } catch (_error) {
      this.locations = [];
    }
  });

  loadFacets = restartableTask(async () => {
    try {
      const companyId = this.session.companyId;
      if (!companyId) {
        throw new Error('No company ID available');
      }

      const response = await fetch(urlBuilder.eventLogs.facets(parseInt(companyId, 10)), {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load facets: ${response.statusText}`);
      }

      const payload = (await response.json()) as EventLogFacets;

      this.facets = {
        event: payload.event ?? [],
        actorType: payload.actorType ?? [],
        productArea: payload.productArea ?? [],
        actorName: payload.actorName ?? [],
      };
    } catch (_error) {
      this.facets = {
        event: [],
        actorType: [],
        productArea: [],
        actorName: [],
      };
    }
  });

  @action
  onLocationChange(option: LocationOption | null): void {
    const locationId = option?.location ? parseInt(option.location.id, 10) : null;
    this.args.onLocationChange(locationId);

    // track location filter changes (only on global)
    this.metrics.trackEvent('Event Log Location Filter Changed', {
      selected_location_id: locationId,
      selected_location_name: option?.location?.name || null,
      filter_cleared: locationId === null,
      context: 'global_event_log',
      scope: 'global',
    });
  }

  @action
  onProductAreasChange(selectedOptions: string[]): void {
    this.args.onProductAreasChange(selectedOptions);
  }

  @action
  onActorsChange(selectedOptions: string[]): void {
    this.args.onActorsChange(selectedOptions);
  }

  @action
  onEventTypesChange(selectedOptions: string[]): void {
    this.args.onEventTypesChange(selectedOptions);
  }

  <template>
    <div class="bg-carbon-5 rounded p-3">
      <div class="flex items-center">
        <div class="float-left w-3/4">
          <input
            data-test-search-input
            placeholder="Search keywords"
            type="search"
            class="pageSearch border !rounded-md !max-w-full icon-search {{if (gt @search.length 0) '!pr-8'}}"
            id="events-search"
            aria-label="Search event log"
            {{on "input" @onSearchInput}}
            value={{@search}}
          />
          <UiButton
            data-test-clear-search-button
            class="icon-cross-small-gray btn--clear-search {{unless (gt @search.length 0) 'hidden'}}"
            {{on "click" (preventDefault @onSearchClear)}}
          >
            <span class="sr-only">Clear search</span>
          </UiButton>
        </div>
        <div class="float-left w-1/4 pl-4">
          <DateRanger
            data-test-date-filter-button
            data-test-date-range-picker
            class="w-full text-center"
            @startDate={{@dateFrom}}
            @endDate={{@dateTo}}
            @onDateSelected={{@onDateRangeChange}}
            @onDateRangeSelected={{@onDateRangeChange}}
          />
        </div>
      </div>

      {{#if this.showAdvancedFilters}}
        <div class="mt-3 flex items-center gap-4">
          <div class="flex items-center">
            <EventLogDropdownFilter
              class="w-64 !mb-0"
              @options={{this.facets.productArea}}
              @selected={{this.selectedProductAreas}}
              @onChange={{this.onProductAreasChange}}
              @placeholder="Category"
            />
          </div>

          <div class="flex items-center">
            <EventLogDropdownFilter
              class="w-64 !mb-0"
              @options={{this.facets.actorName}}
              @selected={{this.selectedActors}}
              @onChange={{this.onActorsChange}}
              @placeholder="Actor"
            />
          </div>

          <div class="flex items-center">
            <EventLogDropdownFilter
              class="w-64 !mb-0"
              @options={{this.facets.event}}
              @selected={{this.selectedEventTypes}}
              @onChange={{this.onEventTypesChange}}
              @placeholder="Event"
            />
          </div>

          {{#if this.isGlobal}}
            <div class="flex items-center">
              <PowerSelect
                data-test-location-selector
                class="w-48 !mb-0"
                @options={{this.locationOptions}}
                @selected={{this.selectedLocation}}
                @onChange={{this.onLocationChange}}
                @placeholder="Location"
                @allowClear={{true}}
                @searchEnabled={{true}}
                @searchField="location.name"
                as |option|
              >
                <span data-test-location-option="{{option.location.id}}">{{option.location.name}}</span>
              </PowerSelect>
            </div>
          {{/if}}
        </div>
      {{else if this.isGlobal}}
        <div class="mt-3 flex items-center gap-4">
          <div class="flex items-center">
            <PowerSelect
              data-test-location-selector
              class="w-48 !mb-0"
              @options={{this.locationOptions}}
              @selected={{this.selectedLocation}}
              @onChange={{this.onLocationChange}}
              @placeholder="Location"
              @allowClear={{true}}
              @searchEnabled={{true}}
              @searchField="location.name"
              as |option|
            >
              <span data-test-location-option="{{option.location.id}}">{{option.location.name}}</span>
            </PowerSelect>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
